<template>
  <div class="mt-3 relative">
    <div
      ref="carousel"
      class="slim flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1"
    >
      <button
        v-for="(img, id) in images"
        :key="`${id}-img`"
        ref="thumbs"
        type="button"
        :aria-label="`Show image ${id + 1}`"
        :aria-pressed="img === current"
        class="snap-start shrink-0 h-16 w-16 md:h-20 md:w-20 rounded-lg border-2 bg-white p-1 shadow-sm flex items-center justify-center cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-boba-brown focus-visible:ring-offset-2 focus-visible:ring-offset-boba-sand"
        :class="
          img === current
            ? 'border-boba-brown'
            : 'border-transparent hover:border-boba-grey'
        "
        @click="chooseImage(img)"
      >
        <img
          class="max-h-full max-w-full object-contain rounded-md"
          :src="img"
          :alt="`Product Image ${id + 1}`"
        />
      </button>
    </div>

    <fade-transition>
      <div
        v-if="showLeftTransition"
        class="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-boba-sand to-transparent"
      ></div>
    </fade-transition>
    <fade-transition>
      <div
        v-if="showRightTransition"
        class="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-boba-sand to-transparent"
      ></div>
    </fade-transition>
  </div>
</template>
<script setup lang="ts">
import { ref, onMounted, nextTick, onUnmounted } from "vue";

defineProps<{
  images: string[];
  /** The image currently shown large; highlights its thumbnail. */
  current?: string | null;
}>();

const emit = defineEmits(["choose"]);
const carousel = ref<HTMLElement | null>(null);
const thumbs = ref<HTMLElement[]>([]);
const showLeftTransition = ref(false);
const showRightTransition = ref(true);

const observers: { left: IntersectionObserver | null; right: IntersectionObserver | null } = {
  left: null,
  right: null,
};

function chooseImage(img: string) {
  emit("choose", img);
}

onMounted(() => {
  nextTick(() => {
    const first = thumbs.value[0];
    const last = thumbs.value[thumbs.value.length - 1];
    if (!first || !last || typeof IntersectionObserver === "undefined") {
      showRightTransition.value = false;
      return;
    }

    observers.left = new IntersectionObserver(
      (elements) => {
        showLeftTransition.value = !elements[0].isIntersecting;
      },
      { root: carousel.value, threshold: 0.98 },
    );
    observers.right = new IntersectionObserver(
      (elements) => {
        showRightTransition.value = !elements[0].isIntersecting;
      },
      { root: carousel.value, threshold: 0.99 },
    );

    observers.left.observe(first);
    observers.right.observe(last);
  });
});

onUnmounted(() => {
  observers.left?.disconnect();
  observers.right?.disconnect();
});
</script>
